import CoreUtils from "./CoreUtils";
import NumberUtils from "./NumberUtils";

export default class DateTimeUtils {
  static isValidDate(date) {
    if (CoreUtils.isUndefinedOrNull(date))
      return false;

    if (typeof date === 'string')
      date = new Date(date);
    else if (!(date instanceof Date))
      return false;

    return date.toString() !== 'Invalid Date';
  }

  static getNow() {
    return new Date();
  }

  static getToday() {
    return DateTimeUtils.getDate(DateTimeUtils.getNow());
  }

  static getDate(time) {
    if (!(time instanceof Date))
      return null;

    return new Date(time.getFullYear(), time.getMonth(), time.getDate());
  }

  static getThisMonth() {
    var localNow = DateTimeUtils.getNow();
    return new Date(localNow.getFullYear(), localNow.getMonth(), 1);
  }

  static areDatesEqual(date1, date2) {
    if (!(date1 instanceof Date) || !(date1 instanceof Date))
      return false;

    return date1.toUTCString() === date2.toUTCString();
  }

  static getMonth(date) {
    if (!(date instanceof Date))
      return date;

    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  static getNextMonth() {
    var thisMonth = DateTimeUtils.getThisMonth();

    var year = thisMonth.getFullYear();
    var month = thisMonth.getMonth();

    month += 1;
    if (month >= 12) {
      month = 0;
      year += 1;
    }

    return new Date(year, month, 1);
  }

  static isDateOnly(date) {
    if (CoreUtils.isUndefinedOrNull(date))
      return false;

    if (typeof date === 'string')
      date = new Date(date);
    else if (!(date instanceof Date))
      return false;

    return date.getHours() === 0
      && date.getMinutes() === 0
      && date.getSeconds() === 0
      && date.getMilliseconds() === 0;
  }

  static isFirstDayOfMonth(date) {
    if (CoreUtils.isUndefinedOrNull(date))
      return false;

    if (typeof date === 'string')
      date = new Date(date);
    else if (!(date instanceof Date))
      return false;

    return date.getDate() === 1;
  }

  static isLastDayOfMonth(date) {
    if (CoreUtils.isUndefinedOrNull(date))
      return false;

    if (typeof date === 'string')
      date = new Date(date);
    else if (!(date instanceof Date))
      return false;

    return date.getDate() === DateTimeUtils.getDaysInMonth(date.getFullYear(), date.getMonth() + 1);
  }

  static nudgeDateToFirstDayOfMonth(date) {
    if (CoreUtils.isUndefinedOrNull(date))
      return null;

    var isStringDate = false;
    if (typeof date === 'string') {
      date = new Date(date);
      isStringDate = true;
    }
    else if (!(date instanceof Date))
      return null;

    date = new Date(
      date.getFullYear(),
      date.getMonth(),
      1
    );

    if (isStringDate)
      date = DateTimeUtils.toDtoDateTime(date);

    return date;
  }

  static nudgeDateToLastDayOfMonth(date) {
    if (CoreUtils.isUndefinedOrNull(date))
      return null;

    var isStringDate = false;
    if (typeof date === 'string') {
      date = new Date(date);
      isStringDate = true;
    }
    else if (!(date instanceof Date))
      return null;

    date = new Date(
      date.getFullYear(),
      date.getMonth(),
      DateTimeUtils.getDaysInMonth(date.getFullYear(), date.getMonth() + 1)
    );

    if (isStringDate)
      date = DateTimeUtils.toDtoDateTime(date);

    return date;
  }

  static toDtoDateTime(date) {
    if (CoreUtils.isUndefinedOrNull(date) || !(date instanceof Date))
      return '';

    return `${NumberUtils.padZerosIfNeeded(date.getFullYear(), 4)}-`
      + `${NumberUtils.padZerosIfNeeded(date.getMonth() + 1, 2)}-`
      + `${NumberUtils.padZerosIfNeeded(date.getDate(), 2)}T`
      + `${NumberUtils.padZerosIfNeeded(date.getHours(), 2)}:`
      + `${NumberUtils.padZerosIfNeeded(date.getMinutes(), 2)}:`
      + `${NumberUtils.padZerosIfNeeded(date.getSeconds(), 2)}`;
  }

  static parseDtoDateTime(dtoDateTime) {
    return CoreUtils.isDefinedAndNotNull(dtoDateTime)
      ? Date.parse(dtoDateTime)
      : null;
  }

  static formatDateForQuery(month) {
    return NumberUtils.padZerosIfNeeded(month.getFullYear(), 4) + '-'
      + NumberUtils.padZerosIfNeeded((month.getMonth() + 1), 2) + '-'
      + NumberUtils.padZerosIfNeeded(month.getDate(), 2);
  }

  static getDaysInMonth(year, month) {
    if (isNaN(year) || isNaN(month))
      return null;

    if (month < 1 || month > 12)
      return null;

    switch (month) {
      case 2:
      {
        if (year % 400 === 0)
          return 29;
        if (year % 100 === 0)
          return 28;
        if (year % 4 === 0)
          return 29;
        return 28;
      }
      case 4:
      case 6:
      case 9:
      case 11:
        return 30;
      default:
        return 31;
    }
  }

  static formatMonthDateAndYear(date, locale) {
    if (CoreUtils.isUndefinedOrNull(date))
      return '';

    if (!(date instanceof Date))
      date = new Date(date);

    const dayOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    return date.toLocaleDateString(locale, dayOptions);
  }

  static formatMonthAndYear(date, locale) {
    if (CoreUtils.isUndefinedOrNull(date))
      return '';

    if (!(date instanceof Date))
      date = new Date(date);

    const dayOptions = {
      year: 'numeric',
      month: 'long'
    };

    return date.toLocaleDateString(locale, dayOptions);
  }

  static formatFullShortDate(date, locale) {
    if (CoreUtils.isUndefinedOrNull(date))
      return '';

    if (!(date instanceof Date))
      date = new Date(date);

    return DateTimeUtils.formatShortDate(date, locale)
      + ' (' + DateTimeUtils.formatShortWeekday(date, locale) + ')';
  }

  static formatShortDate(date, locale) {
    if (CoreUtils.isUndefinedOrNull(date))
      return '';

    if (!(date instanceof Date))
      date = new Date(date);

    return date.toLocaleDateString(locale);
  }

  static formatShortWeekday(date, locale) {
    if (CoreUtils.isUndefinedOrNull(date))
      return '';

    if (!(date instanceof Date))
      date = new Date(date);

    const dayOptions = {
      weekday: 'short'
    };

    return date.toLocaleDateString(locale, dayOptions);
  }

  static formatFullShortDateTime(date, locale) {
    if (CoreUtils.isUndefinedOrNull(date))
      return '';

    if (!(date instanceof Date))
      date = new Date(date);

    return DateTimeUtils.formatFullShortDate(date, locale) + ' '
      + DateTimeUtils.formatShortTime(date, locale);
  }

  static formatShortDateTime(date, locale) {
    if (CoreUtils.isUndefinedOrNull(date))
      return '';

    if (!(date instanceof Date))
      date = new Date(date);

    return DateTimeUtils.formatShortDate(date, locale) + ' '
      + DateTimeUtils.formatShortTime(date, locale);
  }

  static formatShortTime(date, locale) {
    if (CoreUtils.isUndefinedOrNull(date))
      return '';

    if (!(date instanceof Date))
      date = new Date(date);

    return date.toLocaleTimeString(locale);
  }
}